<template>
    <span class="isolate inline-flex overflow-hidden rounded-md shadow-sm">
        <ToolTip
            v-if="planLabel"
            :title="planLabel"
            :description="planDescription"
        >
            <template #trigger>
                <span
                    class="inline-flex items-center px-3 py-2 text-sm font-semibold ring-1 ring-inset backdrop-blur cursor-default"
                    :class="labelClasses"
                    data-test="property-plan-label"
                >
                    {{ planLabel }}
                </span>
            </template>
        </ToolTip>

        <button
            v-if="canUpgrade"
            type="button"
            data-test="property-upgrade-button"
            class="relative inline-flex items-center gap-2 bg-gold-500 px-3 py-2 text-sm font-bold text-[#7d6310] hover:bg-gold-700 focus:z-10"
            @click="$emit('upgrade')"
        >
            {{ upgradeLabel }}
            <font-awesome-icon v-if="upgradeLabel === 'Upgrade to Pro'" :icon="faStars" class="text-lg" aria-hidden="true" />
        </button>
        <button
            v-else
            type="button"
            title="Share property application link"
            data-test="property-share-button"
            :disabled="shareDisabled"
            class="relative inline-flex items-center gap-2 bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white hover:opacity-90 focus:z-10 focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            @click="$emit('share')"
        >
            Share
            <font-awesome-icon :icon="faArrowUpRightFromSquare" aria-hidden="true" />
        </button>
    </span>
</template>

<script setup>
import { computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faArrowUpRightFromSquare, faStars } from '@fortawesome/pro-solid-svg-icons'
import ToolTip from '@/Shared/ToolTip.vue'

// 86bc2cgxd: on the property details hero the plan sits as a label attached to
// the action it invites — Upgrade to Pro on a property without Pro features, Share
// on a property that already has every feature.
// 86bcd9hg3: Pro is account-wide, so the wording is about the account, not the
// property, and there is no per-property downgrade any more.
const PLANS = {
    riskguard_pro: {
        label: 'Pro Plan',
        classes: 'bg-[#f7ecc4] text-[#7d6310] ring-[#e0cb85]',
        description: 'Your account is on RiskGuard Pro, so every property has full feature access.',
    },
    riskguard: {
        label: 'RiskGuard Plan',
        classes: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
        description: 'This property is on your RiskGuard plan.',
    },
    default: {
        label: 'Basic Plan',
        classes: 'bg-slate-100 text-slate-600 ring-slate-400/40',
        description: 'This property is on the Basic Plan and has access to tenant screening. Upgrade your account to RiskGuard Pro at any time to get full feature access on every property.',
    },
}

const props = defineProps({
    // The badge object from PropertyShowResource ({ value, label, … }); null on
    // an archived property, where no plan label is shown.
    tier: {
        type: Object,
        default: null,
    },
    canUpgrade: {
        type: Boolean,
        default: false,
    },
    // "Upgrade to Pro", or "Update card" when the landlord's Pro payment failed.
    upgradeLabel: {
        type: String,
        default: 'Upgrade to Pro',
    },
    shareDisabled: {
        type: Boolean,
        default: false,
    },
})

defineEmits(['upgrade', 'share'])

const plan = computed(() => (props.tier ? PLANS[props.tier.value] ?? PLANS.default : null))
const planLabel = computed(() => plan.value?.label ?? '')
const planDescription = computed(() => plan.value?.description ?? '')
const labelClasses = computed(() => plan.value?.classes ?? '')
</script>
